import rawProductsData from './products.json';
import { LINE_EXPERIENCE, getFinderModelByProductId, getProductShopUrl } from './storeCatalog';

export interface ProductSpecs {
  [key: string]: string | undefined;
  range: string;
  maxSpeed: string;
  motor: string;
  motorMax?: string;
  weight: string;
  battery: string;
  wheels: string;
  frontBrake?: string;
  rearBrake?: string;
  brake?: string;
  frontSuspension?: string;
  rearSuspension?: string;
  suspension?: string;
  traction?: string;
  maxLoad: string;
  chargingTime: string;
  maxSlope: string;
}

export interface Product {
  id: string;
  lineId: string;
  lineName: string;
  name: string;
  slogan: string;
  image: string;
  images?: string[];  // 新增：产品图片数组
  price?: number;
  originalPrice?: number;
  specs: ProductSpecs;
  colors?: string[];
  highlights: string[];
  link: string;
  shopUrl: string;
  useCases: string[];
  technicalCallout?: string;
  accessoryCategoryIds: string[];
  partCategoryIds: string[];
  finderShopUrl?: string;
}

export interface ProductLine {
  id: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  coverImage: string;
  backgroundColor: string;
  products: Product[];
}

export interface ProductsData {
  productLines: ProductLine[];
}

interface RawProduct extends Omit<Product, 'lineId' | 'lineName' | 'link' | 'shopUrl' | 'useCases' | 'technicalCallout' | 'accessoryCategoryIds' | 'partCategoryIds' | 'finderShopUrl'> {
  link?: string;
}

interface RawProductLine extends Omit<ProductLine, 'products'> {
  products: RawProduct[];
}

interface RawProductsData {
  productLines: RawProductLine[];
}

export function getProductsPath() {
  return '/patinetes-electricos';
}

export function buildProductPath(lineId: string, productId: string) {
  return `${getProductsPath()}/${lineId}/${productId}`;
}

function getProductExperience(lineId: string) {
  return LINE_EXPERIENCE[lineId] ?? {
    accessoryCategoryIds: [],
    partCategoryIds: [],
    useCases: []
  };
}

function normalizeProductLine(line: RawProductLine): ProductLine {
  const experience = getProductExperience(line.id);

  return {
    ...line,
    products: line.products.map((product) => ({
      ...product,
      lineId: line.id,
      lineName: line.name,
      link: buildProductPath(line.id, product.id),
      shopUrl: getProductShopUrl(line.id, product.id),
      useCases: experience.useCases,
      technicalCallout: product.slogan,
      accessoryCategoryIds: experience.accessoryCategoryIds,
      partCategoryIds: experience.partCategoryIds,
      finderShopUrl: getFinderModelByProductId(product.id)?.shopUrl
    }))
  };
}

// 类型断言确保 JSON 数据符合接口定义，并在加载时补齐官网所需字段
export const PRODUCTS_DATA: ProductsData = {
  productLines: (rawProductsData as RawProductsData).productLines.map(normalizeProductLine)
};

// 获取所有产品线
export function getProductLines(): ProductLine[] {
  return PRODUCTS_DATA.productLines;
}

// 根据 ID 获取产品线
export function getProductLineById(id: string): ProductLine | undefined {
  return PRODUCTS_DATA.productLines.find(line => line.id === id);
}

// 获取所有产品
export function getAllProducts(): Product[] {
  return PRODUCTS_DATA.productLines.flatMap(line => line.products);
}

// 根据 ID 获取产品
export function getProductById(id: string): Product | undefined {
  return getAllProducts().find(product => product.id === id);
}

export function getProductByRoute(lineId: string, productId: string): Product | undefined {
  const line = getProductLineById(lineId);
  return line?.products.find((product) => product.id === productId);
}

// 获取热门产品（根据 PRD 1.7 要求）
export function getFeaturedProducts() {
  const zwolf = getProductLineById('zwolf');
  const zfox = getProductLineById('zfox');
  const zlion = getProductLineById('zlion');
  const zrino = getProductLineById('zrino');
  const zcougar = getProductLineById('zcougar');

  return {
    // 最新发布: ZWolf Duo
    latestRelease: zwolf?.products.find(p => p.id === 'zwolf-duo'),

    // 最城市: ZFox Max / ZFox Plus / ZLion Pro
    urbanBest: {
      zfoxMax: zfox?.products.find(p => p.id === 'zfox-max'),
      zfoxPlus: zfox?.products.find(p => p.id === 'zfox-plus'),
      zlionPro: zlion?.products.find(p => p.id === 'zlion-pro'),
    },

    // 高续航: ZRino Duo Max / ZCougar Duo
    longRange: {
      zrinoDuoMax: zrino?.products.find(p => p.id === 'zrino-duo-max'),
      zcougarDuo: zcougar?.products.find(p => p.id === 'zcougar-duo'),
    }
  };
}
